import {
  Commend3BadgeState,
  Commend3GenerationStatus,
  Commend3Model,
  Commend3Strategy,
  Commend3StrategyMode,
  Commend3SubModel,
} from "./Types";
import {
  GENERATING_LABEL,
  GENERATION_STATUS,
  NOT_CONFIGURED_LABEL,
  NOT_GENERATED_LABEL,
  PROMOTION_STATUS,
} from "./Commend3Constants";

export const formatTimestampToUTC = (
  timestamp: string | number | null | undefined
): string | null => {
  if (timestamp === null || timestamp === undefined || timestamp === "" || timestamp === "null") {
    return null;
  }

  try {
    let date: Date;

    if (typeof timestamp === "number") {
      date = timestamp > 9999999999 ? new Date(timestamp) : new Date(timestamp * 1000);
    } else {
      const numericTimestamp = Number(timestamp);
      if (timestamp.trim() !== "" && !isNaN(numericTimestamp)) {
        date =
          numericTimestamp > 9999999999
            ? new Date(numericTimestamp)
            : new Date(numericTimestamp * 1000);
      } else {
        date = new Date(timestamp);
      }
    }

    if (isNaN(date.getTime())) return null;

    const day = date.getUTCDate();
    const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;

    return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm} UTC`;
  } catch {
    return null;
  }
};

export const formatDateOnly = (
  timestamp: string | number | null | undefined
): string | null => {
  const utc = formatTimestampToUTC(timestamp);
  if (!utc) return null;
  const [datePart] = utc.split(",");
  const segments = datePart.trim().split(" ");
  if (segments.length < 3) return datePart.trim();
  const [day, month, year] = segments;
  return `${day.padStart(2, "0")}-${month}-${year}`;
};

export const getPrimarySubModel = (model: Commend3Model): Commend3SubModel | null => {
  const subModels = model?.sub_models;
  if (!Array.isArray(subModels) || subModels.length === 0) return null;
  return subModels[0];
};

export const getStrategyLabel = (model: Commend3Model): string => {
  const subModel = getPrimarySubModel(model);
  const strategyName = subModel?.strategy_name?.trim();
  return strategyName ? strategyName : NOT_CONFIGURED_LABEL;
};

export const hasStrategy = (model: Commend3Model): boolean =>
  Boolean(getPrimarySubModel(model)?.strategy_name?.trim());

export const isPromoted = (model: Commend3Model): boolean =>
  model?.promotion_status?.status === PROMOTION_STATUS.PROMOTED;

export const isMappedToWidget = (model: Commend3Model): boolean =>
  Boolean(model?.widget?.id);

export const isLive = (model: Commend3Model): boolean =>
  isPromoted(model) && isMappedToWidget(model);

export const isTransient = (model: Commend3Model): boolean =>
  model?.promotion_status?.status === PROMOTION_STATUS.PROMOTING;

export type Commend3BadgeDescriptor = {
  state: Commend3BadgeState;
  label: string;
  showAlert: boolean;
};

export const resolvePromotionBadge = (model: Commend3Model): Commend3BadgeDescriptor => {
  const status = model?.promotion_status?.status;
  const regenerated = Boolean(model?.promotion_status?.regenerated);

  if (status === PROMOTION_STATUS.PROMOTING) {
    return { state: "promoting", label: "Promoting…", showAlert: false };
  }

  if (status === PROMOTION_STATUS.PROMOTED) {
    if (isMappedToWidget(model)) {
      return { state: "live", label: "Live", showAlert: regenerated };
    }
    return { state: "promoted", label: "Promoted", showAlert: regenerated };
  }

  return { state: "notPromoted", label: "Not Promoted", showAlert: false };
};

export const getUpdatedByName = (model: Commend3Model): string =>
  model?.updated_by?.name?.trim() || "";

export const getWidgetLabel = (model: Commend3Model): string | null =>
  model?.widget?.name?.trim() || null;

export type Commend3GenerationTone = "fresh" | "stale" | "muted";

export type Commend3GenerationLine = {
  text: string;
  tone: Commend3GenerationTone;
};

export const isStrategyGenerated = (strategy: Commend3Strategy): boolean =>
  strategy?.generation_status?.status === GENERATION_STATUS.GENERATED;

export const isStrategyGenerating = (strategy: Commend3Strategy): boolean =>
  strategy?.generation_status?.status === GENERATION_STATUS.GENERATING;

export const isStrategyStale = (strategy: Commend3Strategy): boolean =>
  Boolean(strategy?.generation_status?.reconfigured);

export const resolveGenerationLine = (
  strategy: Commend3Strategy
): Commend3GenerationLine => {
  if (isStrategyGenerating(strategy)) {
    return { text: GENERATING_LABEL, tone: "muted" };
  }

  if (isStrategyGenerated(strategy)) {
    const formatted = formatTimestampToUTC(strategy?.generation_status?.generated_at);
    if (formatted) {
      return {
        text: `Last generated: ${formatted}`,
        tone: isStrategyStale(strategy) ? "stale" : "fresh",
      };
    }
  }

  return { text: NOT_GENERATED_LABEL, tone: "muted" };
};

export const generationUsageLabel = (strategy: Commend3Strategy): string => {
  const used = strategy?.generation_count ?? 0;
  const limit = strategy?.monthly_generation_limit;
  if (limit === null || limit === undefined) {
    return `${used} ${used === 1 ? "generation" : "generations"} used this month`;
  }
  const remaining = Math.max(limit - used, 0);
  return `${used} of ${limit} generations used this month · ${remaining} remaining`;
};

export const isGenerationLimitReached = (strategy: Commend3Strategy): boolean => {
  const limit = strategy?.monthly_generation_limit;
  if (limit === null || limit === undefined) return false;
  return (strategy?.generation_count ?? 0) >= limit;
};

export const getInScopeModes = (subModel: Commend3SubModel | null): Commend3StrategyMode[] =>
  subModel?.modes?.in_scope ?? [];

export const getOutOfScopeModes = (
  subModel: Commend3SubModel | null
): Commend3StrategyMode[] =>
  subModel?.modes?.out_of_scope ?? subModel?.modes?.out_scope ?? [];

export const isModelLocked = (model: Commend3Model | null): boolean => {
  if (!model) return false;
  const status = model?.promotion_status?.status;
  return (
    status === PROMOTION_STATUS.PROMOTED || status === PROMOTION_STATUS.PROMOTING
  );
};

export const getStrategyOptionsForModel = (
  strategies: Commend3Strategy[],
  model: Commend3Model | null
): Commend3Strategy[] => {
  if (!model) return [];
  return strategies.filter((strategy) => strategy.type === model.type);
};

export const isSubstitutionModel = (model: Commend3Model | null): boolean =>
  model?.type === "Substitution";

export const strategyGeneratedLabel = (strategy: Commend3Strategy): string => {
  const formatted = formatTimestampToUTC(strategy?.generation_status?.generated_at);
  return formatted ? `Generated ${formatted}` : "Not generated yet";
};

export const getLinkedStrategyGeneration = (
  model: Commend3Model
): Commend3GenerationStatus | null => {
  const subModel = getPrimarySubModel(model);
  if (!subModel) return null;
  return (
    subModel.generation_status ??
    subModel.strategy?.generation_status ??
    subModel.strategy_details?.generation_status ??
    null
  );
};

export type Commend3GenerationBadge = {
  variant: "generated" | "notGenerated" | "generating";
  label: string;
  timestamp: string | null;
};

export const resolveGenerationBadge = (
  model: Commend3Model
): Commend3GenerationBadge | null => {
  const generation = getLinkedStrategyGeneration(model);
  if (!generation) return null;

  if (generation.status === GENERATION_STATUS.GENERATING) {
    return { variant: "generating", label: "Generating", timestamp: null };
  }
  if (generation.status === GENERATION_STATUS.GENERATED) {
    return {
      variant: "generated",
      label: "Generated",
      timestamp: formatTimestampToUTC(generation.generated_at),
    };
  }
  return { variant: "notGenerated", label: "Not generated", timestamp: null };
};
